import type { Employee, OnboardingInfo, OnboardingStatus, OnboardingSubmission } from '../api/types'

/** Everything the onboarding form holds while it is being filled in. Plain strings and booleans, so it can be seeded from what is
 * on file and turned into a request (see `toSubmission`). The tax file number and member number are never in here until the
 * person types them: the server only ever sends back a masked tail. */
export interface OnboardingFormState {
  mobile: string
  homeAddress: string
  dateOfBirth: string
  contactName: string
  contactRelationship: string
  contactDaytimePhone: string
  contactAfterHours: string
  abn: string
  tfn: string
  noTfn: boolean
  noTfnReason: string
  taxResident: boolean
  taxFreeThreshold: boolean
  helpDebt: boolean
  fundType: 'REGULATED' | 'SMSF'
  fundName: string
  usi: string
  fundAbn: string
  memberNumber: string
}

export function initialFormState(info: OnboardingInfo): OnboardingFormState {
  const tax = info.tax
  const fund = info.super
  const contact = info.emergencyContact
  return {
    mobile: info.personal.mobile ?? '',
    homeAddress: info.personal.homeAddress ?? '',
    dateOfBirth: info.personal.dateOfBirth ?? '',
    contactName: contact?.name ?? '',
    contactRelationship: contact?.relationship ?? '',
    contactDaytimePhone: contact?.daytimePhone ?? '',
    contactAfterHours: contact?.afterHoursMobile ?? '',
    abn: info.abn ?? '',
    tfn: '',
    // Someone who earlier said why they have no number starts with that box ticked; one with a number on file does not.
    noTfn: !!tax && !tax.hasTfn && !!tax.noTfnReason,
    noTfnReason: tax && !tax.hasTfn ? (tax.noTfnReason ?? '') : '',
    taxResident: tax?.taxResident ?? true,
    taxFreeThreshold: tax?.taxFreeThreshold ?? true,
    helpDebt: tax?.helpDebt ?? false,
    fundType: fund?.fundType ?? 'REGULATED',
    fundName: fund?.fundName ?? '',
    usi: fund?.usi ?? '',
    fundAbn: fund?.fundAbn ?? '',
    memberNumber: '',
  }
}

const orNull = (s: string): string | null => (s.trim() === '' ? null : s.trim())

/** Only the sections the person is asked for go in the request: tax and super for most people, an ABN for a subcontractor, a
 * date of birth where it is wanted. The server validates everything, so this does no checking of its own beyond tidying. */
export function toSubmission(info: OnboardingInfo, f: OnboardingFormState): OnboardingSubmission {
  return {
    mobile: f.mobile.trim(),
    homeAddress: f.homeAddress.trim(),
    dateOfBirth: info.requiresDateOfBirth ? orNull(f.dateOfBirth) : null,
    emergencyContact: {
      name: f.contactName.trim(),
      relationship: orNull(f.contactRelationship),
      daytimePhone: orNull(f.contactDaytimePhone),
      afterHoursMobile: orNull(f.contactAfterHours),
    },
    tax: info.requiresTaxAndSuper
      ? {
          // "I don't have one" sends the reason instead of a number; leaving both blank keeps any number already on file.
          tfn: f.noTfn ? null : orNull(f.tfn),
          noTfnReason: f.noTfn ? orNull(f.noTfnReason) : null,
          taxResident: f.taxResident,
          foreignResident: !f.taxResident,
          taxFreeThreshold: f.taxFreeThreshold,
          helpDebt: f.helpDebt,
        }
      : null,
    super: info.requiresTaxAndSuper
      ? {
          fundType: f.fundType,
          fundName: f.fundName.trim(),
          usi: orNull(f.usi),
          fundAbn: f.fundType === 'SMSF' ? orNull(f.fundAbn) : null,
          memberNumber: orNull(f.memberNumber),
        }
      : null,
    abn: info.requiresAbn ? orNull(f.abn) : null,
  }
}

/** Whether the portal should stop someone at the onboarding page. A server too old to say anything about it asks nothing. */
export const mustOnboard = (status: OnboardingStatus | undefined): boolean => status === 'REQUIRED'

/** Anyone still to confirm their details: asked and not done (REQUIRED), or never asked (NONE). Completed people are left out. */
export const canBeAsked = (e: Pick<Employee, 'active' | 'onboardingStatus'>): boolean => e.active && e.onboardingStatus !== 'COMPLETE'
